import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SUPPORTED_LANGUAGES, resolveTranslation } from '../utils/translatable-string';
import { useResolveTranslation } from '../hooks/use-resolve-translation.hook';

interface TranslatableChipInputProps {
  value: (string | Record<string, string>)[];
  onChange: (val: (string | Record<string, string>)[]) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
}

const LANG_LABELS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  ar: 'AR',
  es: 'ES',
};

/**
 * TranslatableChipInput — For editing translatable option arrays.
 *
 * Chips display current language value. Add form shows inputs for all 4 languages.
 * English is required; other languages are optional.
 */
export const TranslatableChipInput: React.FC<TranslatableChipInputProps> = ({
  value,
  onChange,
  label,
  required = false,
  placeholder,
}) => {
  const { rt } = useResolveTranslation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOption, setNewOption] = useState<Record<string, string>>({ en: '', fr: '', ar: '', es: '' });

  const handleAdd = () => {
    if (!newOption.en.trim()) return;

    // Clean: only include languages that have values
    const cleaned: Record<string, string> = {};
    for (const lang of SUPPORTED_LANGUAGES) {
      if (newOption[lang]?.trim()) {
        cleaned[lang] = newOption[lang].trim();
      }
    }

    // If only English is provided, store as plain string for simplicity
    const newVal = Object.keys(cleaned).length === 1 && cleaned.en ? cleaned.en : cleaned;

    onChange([...value, newVal]);
    setNewOption({ en: '', fr: '', ar: '', es: '' });
    setShowAddForm(false);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div>
      {label && (
        <Label className="mb-1.5 block text-xs text-muted-foreground">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}

      <div className="mb-2 flex flex-wrap gap-1.5">
        {value.map((option, index) => (
          <Badge
            key={`${resolveTranslation(option, 'en')}-${index}`}
            variant="outline"
            className="gap-1 py-1 pe-1 ps-2 font-normal"
          >
            <span>{rt(option)}</span>
            <button
              type="button"
              onClick={() => handleRemove(index)}
              aria-label={`Remove ${resolveTranslation(option, 'en')}`}
              className="grid size-4 place-items-center text-muted-foreground transition-colors hover:text-destructive"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>

      {showAddForm ? (
        <div className="border bg-muted p-4">
          <div className="flex flex-col gap-2">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <div key={lang} className="flex items-center gap-2">
                <span className="w-6 text-center text-[11px] font-medium text-muted-foreground">
                  {LANG_LABELS[lang]}
                </span>
                <Input
                  value={newOption[lang] || ''}
                  onChange={(e) => setNewOption({ ...newOption, [lang]: e.target.value })}
                  placeholder={
                    lang === 'en'
                      ? placeholder || 'English value (required)'
                      : `${LANG_LABELS[lang]} translation`
                  }
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  required={lang === 'en'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddForm(false);
                setNewOption({ en: '', fr: '', ar: '', es: '' });
              }}
            >
              Cancel
            </Button>
            <Button variant="outline" size="sm" onClick={handleAdd} disabled={!newOption.en.trim()}>
              <Plus className="size-4" />
              Add
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
          <Plus className="size-4" />
          Add Option
        </Button>
      )}
    </div>
  );
};

export default TranslatableChipInput;
